import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';

import axiosInstance from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';

import OnboardingProgressTracker from './OnboardingProgressTracker';
import WelcomeStep from './steps/WelcomeStep';
import CompanyDetailsStep from './steps/CompanyDetailsStep';
import FiscalYearStep from './steps/FiscalYearStep';
import LogoUploadStep from './steps/LogoUploadStep';
import CompletionStep from './steps/CompletionStep';

const STEPS = {
    WELCOME: 'step_welcome',
    COMPANY_DETAILS: 'step_company_details',
    FISCAL_YEAR: 'step_fiscal_year',
    LOGO: 'step_logo',
    DONE: 'step_done'
};

const STEP_ORDER = [STEPS.WELCOME, STEPS.COMPANY_DETAILS, STEPS.FISCAL_YEAR, STEPS.LOGO, STEPS.DONE];

const STEP_CONFIG = [
    { key: STEPS.WELCOME, label: 'Välkommen' },
    { key: STEPS.COMPANY_DETAILS, label: 'Företaget' },
    { key: STEPS.FISCAL_YEAR, label: 'Räkenskapsår' },
    { key: STEPS.LOGO, label: 'Logotyp' },
    { key: STEPS.DONE, label: 'Klart' },
];

const OnboardingWizard = () => {
    const { companyId } = useParams();
    const history = useHistory();

    const [activeStep, setActiveStep] = useState(STEPS.WELCOME);
    const [onboardingStatus, setOnboardingStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [completedSteps, setCompletedSteps] = useState([]);

    // Form state
    const [companyDetails, setCompanyDetails] = useState({
        contactPerson: '',
        companyEmail: '',
        companyPhone: ''
    });
    const [fiscalYearData, setFiscalYearData] = useState({
        startDate: `${new Date().getFullYear()}-01-01`,
        endDate: `${new Date().getFullYear()}-12-31`,
        accountingMethod: 'accrual',
        vatPeriod: 'monthly'
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    useEffect(() => {
        fetchOnboardingStatus();
    }, [companyId]);

    const fetchOnboardingStatus = async () => {
        try {
            const url = getApiUrl(`${companyId}/onboarding/status`);
            const response = await axiosInstance.get(url);
            const status = response.data;
            setOnboardingStatus(status);

            // If wizard already completed, redirect to dashboard
            if (status.wizardCompleted) {
                history.replace(`/${companyId}/dashboard`);
                return;
            }

            // Pre-fill form data from status
            if (status.steps.companyDetails.data) {
                setCompanyDetails({
                    contactPerson: status.steps.companyDetails.data.contactPerson || '',
                    companyEmail: status.steps.companyDetails.data.companyEmail || '',
                    companyPhone: status.steps.companyDetails.data.companyPhone || ''
                });
            }

            // Seed the fiscal-year step from the choices made at registration
            // (/create-company stores them on company_details; without this
            // the step silently falls back to hardcoded defaults and the
            // user's original selection is ignored).
            try {
                const detailsRes = await axiosInstance.get(getApiUrl(`${companyId}/companydetails`));
                const d = detailsRes.data || {};
                // company_details vocabulary -> fiscal-year vocabulary
                const vatPeriodMap = { month: 'monthly', quarter: 'quarterly', annual: 'yearly' };
                setFiscalYearData(prev => ({
                    ...prev,
                    accountingMethod: d.accounting_method || prev.accountingMethod,
                    vatPeriod: vatPeriodMap[d.vat_reporting_period] || prev.vatPeriod
                }));
            } catch (e) {
                // Non-fatal — the step keeps its defaults and stays editable
                console.error('Could not prefill fiscal-year defaults from company details:', e);
            }

            // Mark already-completed steps and resume at the first incomplete one
            const completed = [];
            if (status.steps.companyDetails.completed) completed.push(STEPS.COMPANY_DETAILS);
            if (status.steps.fiscalYear.completed) completed.push(STEPS.FISCAL_YEAR);
            if (status.steps.logo.completed) completed.push(STEPS.LOGO);
            setCompletedSteps(completed);

            // Only skip ahead if the user has already completed at least one step
            // (i.e. they started the wizard before). Otherwise show Welcome.
            if (completed.length > 0) {
                const stepsToCheck = [
                    { key: STEPS.COMPANY_DETAILS, done: status.steps.companyDetails.completed },
                    { key: STEPS.FISCAL_YEAR, done: status.steps.fiscalYear.completed },
                    { key: STEPS.LOGO, done: status.steps.logo.completed },
                ];
                const firstIncomplete = stepsToCheck.find(s => !s.done);
                if (firstIncomplete) {
                    setActiveStep(firstIncomplete.key);
                } else {
                    setActiveStep(STEPS.DONE);
                }
            }
        } catch (error) {
            toast.error('Kunde inte ladda onboarding-status');
        } finally {
            setLoading(false);
        }
    };

    const goToStep = (step) => {
        setActiveStep(step);
    };

    const goNext = () => {
        const currentIndex = STEP_ORDER.indexOf(activeStep);
        if (currentIndex < STEP_ORDER.length - 1) {
            setActiveStep(STEP_ORDER[currentIndex + 1]);
        }
    };

    const goBack = () => {
        const currentIndex = STEP_ORDER.indexOf(activeStep);
        if (currentIndex > 0) {
            setActiveStep(STEP_ORDER[currentIndex - 1]);
        }
    };

    const markStepCompleted = (stepKey) => {
        setCompletedSteps(prev => prev.includes(stepKey) ? prev : [...prev, stepKey]);
    };

    const handleCompleteWizard = async () => {
        try {
            setSaving(true);
            const url = getApiUrl(`${companyId}/onboarding/complete-wizard`);
            await axiosInstance.post(url);
            history.push(`/${companyId}/dashboard`);
        } catch (error) {
            toast.error('Kunde inte slutföra guiden');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    const renderStep = () => {
        switch (activeStep) {
            case STEPS.WELCOME:
                return (
                    <WelcomeStep
                        onNext={goNext}
                    />
                );
            case STEPS.COMPANY_DETAILS:
                return (
                    <CompanyDetailsStep
                        companyId={companyId}
                        companyDetails={companyDetails}
                        setCompanyDetails={setCompanyDetails}
                        onNext={goNext}
                        onBack={goBack}
                        saving={saving}
                        setSaving={setSaving}
                        markStepCompleted={() => markStepCompleted(STEPS.COMPANY_DETAILS)}
                        alreadyCompleted={completedSteps.includes(STEPS.COMPANY_DETAILS)}
                    />
                );
            case STEPS.FISCAL_YEAR:
                return (
                    <FiscalYearStep
                        companyId={companyId}
                        fiscalYearData={fiscalYearData}
                        setFiscalYearData={setFiscalYearData}
                        onNext={goNext}
                        onBack={goBack}
                        saving={saving}
                        setSaving={setSaving}
                        markStepCompleted={() => markStepCompleted(STEPS.FISCAL_YEAR)}
                        alreadyCompleted={completedSteps.includes(STEPS.FISCAL_YEAR)}
                    />
                );
            case STEPS.LOGO:
                return (
                    <LogoUploadStep
                        companyId={companyId}
                        logoFile={logoFile}
                        setLogoFile={setLogoFile}
                        logoPreview={logoPreview}
                        setLogoPreview={setLogoPreview}
                        onNext={goNext}
                        onBack={goBack}
                        onSkip={goNext}
                        saving={saving}
                        setSaving={setSaving}
                        markStepCompleted={() => markStepCompleted(STEPS.LOGO)}
                        alreadyCompleted={completedSteps.includes(STEPS.LOGO)}
                    />
                );
            case STEPS.DONE:
                return (
                    <CompletionStep
                        completedSteps={completedSteps}
                        onComplete={handleCompleteWizard}
                        saving={saving}
                        steps={STEPS}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <style>{`
                .onboarding-wizard {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 1rem 0;
                }
                .onboarding-wizard-card {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04);
                    overflow: hidden;
                }
                .onboarding-wizard-body {
                    padding: 2rem;
                }
                @media (max-width: 768px) {
                    .onboarding-wizard-body {
                        padding: 1.25rem;
                    }
                }
            `}</style>
            <div className="onboarding-wizard">
                <Card className="onboarding-wizard-card">
                    <OnboardingProgressTracker
                        steps={STEP_CONFIG}
                        activeStep={activeStep}
                        completedSteps={completedSteps}
                    />
                    <Card.Body className="onboarding-wizard-body">
                        {renderStep()}
                    </Card.Body>
                </Card>
            </div>
        </>
    );
};

export default OnboardingWizard;
